import * as THREE from 'three'

export function createFishEyePass({ renderer }) {
  const renderTarget = new THREE.WebGLRenderTarget(window.innerWidth, window.innerHeight, {
    depthBuffer: true,
    stencilBuffer: false
  })
  renderTarget.texture.name = 'Fish Eye Render Target'

  const uniforms = {
    tDiffuse: { value: renderTarget.texture },
    resolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
    strength: { value: 0 },
    radius: { value: 1 },
    distortionPower: { value: 2 },
    blend: { value: 1 },
    chromaticAberration: { value: 0 },
    zoom: { value: 1 },
    center: { value: new THREE.Vector2(0.5, 0.5) },
    edgeFeather: { value: 0 },
    edgeColor: { value: new THREE.Color(0xffffff) }
  }

  const scene = new THREE.Scene()
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
  const material = new THREE.ShaderMaterial({
    uniforms,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
    vertexShader: `
      varying vec2 vUv;

      void main() {
        vUv = uv;
        gl_Position = vec4(position.xy, 0.0, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D tDiffuse;
      uniform vec2 resolution;
      uniform float strength;
      uniform float radius;
      uniform float distortionPower;
      uniform float blend;
      uniform float chromaticAberration;
      uniform float zoom;
      uniform vec2 center;
      uniform float edgeFeather;
      uniform vec3 edgeColor;

      varying vec2 vUv;

      void main() {
        vec2 aspect = vec2(resolution.x / resolution.y, 1.0);
        vec2 centered = (vUv - center) * aspect;
        float dist = length(centered);
        float safeRadius = max(radius, 0.0001);
        float normalizedDist = dist / safeRadius;
        float radialAmount = pow(normalizedDist, max(distortionPower, 0.0001));
        float distortion = 1.0 + strength * radialAmount;
        vec2 sampleUv = center + centered * distortion / max(zoom, 0.0001) / aspect;
        vec2 aberrationOffset = normalize(centered + vec2(0.0001)) * chromaticAberration * radialAmount / aspect;

        vec4 distortedColor = vec4(
          texture2D(tDiffuse, sampleUv + aberrationOffset).r,
          texture2D(tDiffuse, sampleUv).g,
          texture2D(tDiffuse, sampleUv - aberrationOffset).b,
          texture2D(tDiffuse, sampleUv).a
        );
        vec4 originalColor = texture2D(tDiffuse, vUv);
        vec4 color = mix(originalColor, distortedColor, clamp(blend, 0.0, 1.0));
        float inside = step(0.0, sampleUv.x) * step(sampleUv.x, 1.0) * step(0.0, sampleUv.y) * step(sampleUv.y, 1.0);
        float featherWidth = max(edgeFeather, 0.0001);
        float feather = 1.0 - smoothstep(1.0 - featherWidth, 1.0, normalizedDist);
        gl_FragColor = mix(vec4(edgeColor, 1.0), color, inside * feather);
      }
    `
  })
  scene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material))

  function syncParams(params) {
    uniforms.strength.value = params.strength
    uniforms.radius.value = params.radius
    uniforms.distortionPower.value = params.distortionPower
    uniforms.blend.value = params.blend
    uniforms.chromaticAberration.value = params.chromaticAberration
    uniforms.zoom.value = params.zoom
    uniforms.center.value.set(0.5 + params.centerX * 0.5, 0.5 + params.centerY * 0.5)
    uniforms.edgeFeather.value = params.edgeFeather
    uniforms.edgeColor.value.set(params.edgeColor)
  }

  function updateSize(params) {
    const pixelRatio = renderer.getPixelRatio()
    const supersampling = Number.parseFloat(params.supersampling) || 1
    const width = Math.ceil(window.innerWidth * pixelRatio * supersampling)
    const height = Math.ceil(window.innerHeight * pixelRatio * supersampling)
    renderTarget.setSize(width, height)
    uniforms.resolution.value.set(width, height)
  }

  function render(sourceScene, sourceCamera) {
    uniforms.tDiffuse.value = renderTarget.texture
    renderer.setRenderTarget(renderTarget)
    renderer.render(sourceScene, sourceCamera)
    renderer.setRenderTarget(null)
    renderer.render(scene, camera)
  }

  return {
    syncParams,
    updateSize,
    render
  }
}
